/* eslint-disable indent */
import activityService from 'services/ActivityService';
import publicationService from 'services/PublicationService';
import { getActivityLiveList, getElementPublishedList, getMarketDerivativeList, getLanguagesList } from 'Workspace/Projects/store/selectors';
import { getState, dispatch } from 'pluginServices/store';
import { setActivityLiveList, setElementPublishedList, setMarketDerivativeList, setLanguageList } from 'Workspace/Projects/store/actions';
import featureFlagService, { featureKeys } from 'services/FeatureFlagService';
import { PUBLICATION_STATUS } from 'constants/constants';
class ActivityStatusTypes {
    static LIVE = 'live';
    static OTHER = 'other';
}

class PluginTypes {
    static COURSE = 'course';
}

// Note: Below pluginList mapping is specific to the getCoursewarePublicationStatus/isElementPublished requirements.
const pluginList = {
    "Bronte Viewer eText Plugin": "BRONTE_PEARSON_PLUS",
    "Pearson eText course plugin": "EPUB_ETEXT",
};

const _getActivityLiveStatus = async (startingActivityId, pluginTypeOverride) => {
    const allowGetStatusCall = featureFlagService.is(featureKeys.WORKSPACE_ACTIVITY_GET_ACTIVITY_STATUS);
    if (!allowGetStatusCall || !startingActivityId) return null;

    const rootActivityId = await activityService.getRootActivityId(startingActivityId);
    const { plugin } = await activityService.getActivityPlugIn(rootActivityId);


    let pluginFinal = plugin?.type === PluginTypes.COURSE && pluginList[plugin.name];
    if (pluginTypeOverride) {
        pluginFinal = pluginTypeOverride;
    }
    if (pluginFinal) {
        const res = await publicationService.getCoursewarePublicationStatus(rootActivityId, pluginFinal);
        return res?.publicationCoursewareStatus;
    }
    return null;
};

const _getElementPublishedStatus = async (elementId) => {
    const allowGetStatusCall = featureFlagService.is(featureKeys.WORKSPACE_ACTIVITY_GET_ACTIVITY_STATUS);
    if (!allowGetStatusCall || !elementId) return null;

    const rootActivityId = await activityService.getRootActivityId(elementId);
    const { plugin } = await activityService.getActivityPlugIn(rootActivityId);

    const pluginFinal = plugin?.type === PluginTypes.COURSE && pluginList[plugin.name];
    if (pluginFinal) {
        const isElementPublished = await publicationService.isElementPublished(rootActivityId, elementId);
        return isElementPublished;
    }
    return null;
};

export const getActivityLiveDetails = async (startingActivityId, pluginTypeOverride) => {
    const rootActivityId = await activityService.getRootActivityId(startingActivityId);
    const status = await _getActivityLiveStatus(rootActivityId, pluginTypeOverride);
    if (status) {
        return status;
    }
    return null;
};

export const isActivityLive = async (startingActivityId, forceFetch = false) => {
    const currentActivityList = getActivityLiveList(getState());
    const rootActivityId = await activityService.getRootActivityId(startingActivityId);
    if (currentActivityList && currentActivityList[rootActivityId] && !forceFetch) {
        return currentActivityList[rootActivityId] === ActivityStatusTypes.LIVE;
    }

    const status = await _getActivityLiveStatus(rootActivityId);
    if (status) {
        const isLive = (status?.pearsonPlusStatus === PUBLICATION_STATUS.PUBLISHED_LIVE);
        dispatch(setActivityLiveList({ key: rootActivityId, liveStatus: isLive ? ActivityStatusTypes.LIVE : ActivityStatusTypes.OTHER }));
        return isLive;
    }

    return false;
};

export const checkIfElementPublished = async (elementId, forceFetch) => {
    const currentElementPublishedList = getElementPublishedList(getState());

    if (currentElementPublishedList && currentElementPublishedList[elementId] && !forceFetch) {
        return currentElementPublishedList[elementId];
    }

    const publishStatus = await _getElementPublishedStatus(elementId);
    if (publishStatus) {
        dispatch(setElementPublishedList({ key: elementId, publishStatus }));
        return publishStatus;
    }
    return false;
};

export const getMarketDerivatives = async (coursewareId, forceFetch) => {
    const existingMarketDerivatives = getMarketDerivativeList(getState());
    if (existingMarketDerivatives && existingMarketDerivatives[coursewareId] && !forceFetch) {
        return existingMarketDerivatives[coursewareId];
    }

    const result = await activityService.coursewareConditionalGet(coursewareId);
    if (result?.conditionals) {
        if (result.conditionals.length) { // only save to store if there's data
            dispatch(setMarketDerivativeList({ key: coursewareId, conditionals: result.conditionals }));
        } else {
            dispatch(setMarketDerivativeList({ key: coursewareId, conditionals: null }));
        }
        return result.conditionals;
    }
    return null;
};

export const getLanguages = async (coursewareId, forceFetch) => {
    const existingLanguages = getLanguagesList(getState());
    if (existingLanguages && existingLanguages[coursewareId] && !forceFetch) {
        // Stored entry shape: { key, languages: [...] } — wrap to return { languages: [...] }
        return { languages: existingLanguages[coursewareId].languages };
    }

    const result = await activityService.coursewareLanguagesGet(coursewareId);
    if (result?.languages) {
        if (result.languages.length) { // only save to store if there's data
            // Store the flat array so the entry shape is { key, languages: [...] }
            dispatch(setLanguageList({ key: coursewareId, languages: result.languages }));
        }
        return result; // { languages: [...] }
    }
    return null;
};
